import { useMutation } from "convex/react";
import {
	CheckCircle2Icon,
	ChevronDownIcon,
	CircleDashedIcon,
	CircleDotIcon,
	Loader2Icon,
	OctagonXIcon,
	SkipForwardIcon,
	WorkflowIcon,
} from "lucide-react";
import { useState } from "react";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { api } from "../../../convex/_generated/api";

type WorkflowRunStatus =
	| "running"
	| "awaiting_review"
	| "complete"
	| "failed"
	| "cancelled";

type WorkflowStepStatus =
	| "pending"
	| "running"
	| "awaiting_review"
	| "approved"
	| "failed"
	| "skipped";

export type TrackerStep = {
	name: string;
	title: string;
	status: WorkflowStepStatus;
	revisions?: number;
	transitionReason?: string;
	unmetCriteria?: string[];
	note?: string;
};

export type TrackerRun = {
	runId?: string;
	status: WorkflowRunStatus;
	matterFolderName?: string;
	steps: ReadonlyArray<TrackerStep>;
};

const STATUS_BADGE: Record<WorkflowRunStatus, { label: string; cls: string }> =
	{
		running: { label: "Running", cls: "bg-primary/15 text-primary" },
		awaiting_review: { label: "Needs review", cls: "bg-accent/20 text-accent" },
		complete: { label: "Complete", cls: "bg-primary/15 text-primary" },
		failed: { label: "Failed", cls: "bg-destructive/15 text-destructive" },
		cancelled: { label: "Cancelled", cls: "bg-muted text-muted-foreground" },
	};

export function workflowStepStateLabel(step: TrackerStep): string {
	if (step.status === "approved") {
		return `Approved${step.revisions ? ` · ${step.revisions} revision${step.revisions > 1 ? "s" : ""}` : ""}`;
	}
	if (step.status === "running") return "Working…";
	if (step.status === "awaiting_review") return "Awaiting your review";
	if (step.status === "failed") return "Failed";
	if (step.status === "skipped") return "Skipped";
	return "Pending";
}

function StepRow({ step, onJump }: { step: TrackerStep; onJump?: () => void }) {
	const Icon =
		step.status === "approved"
			? CheckCircle2Icon
			: step.status === "running"
				? Loader2Icon
				: step.status === "awaiting_review"
					? CircleDotIcon
					: step.status === "failed"
						? OctagonXIcon
						: step.status === "skipped"
							? SkipForwardIcon
							: CircleDashedIcon;
	const tone =
		step.status === "approved"
			? "text-primary"
			: step.status === "failed"
				? "text-destructive"
				: step.status === "skipped"
					? "text-muted-foreground"
					: step.status === "awaiting_review"
						? "text-accent"
						: step.status === "pending"
							? "text-muted-foreground/50"
							: "text-foreground";
	const jumpable = onJump !== undefined && step.status !== "pending";
	return (
		<button
			type="button"
			onClick={onJump}
			disabled={!jumpable}
			className={cn(
				"flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
				jumpable ? "hover:bg-muted/60" : "cursor-default",
			)}
		>
			<Icon
				className={cn(
					"mt-0.5 size-3.5 shrink-0",
					tone,
					step.status === "running" && "animate-spin",
				)}
			/>
			<div className="min-w-0 flex-1">
				<div className="truncate text-xs font-medium">{step.title}</div>
				<div className="truncate text-[11px] text-muted-foreground">
					{workflowStepStateLabel(step)}
				</div>
				{step.transitionReason || step.note ? (
					<div className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">
						{step.transitionReason ?? step.note}
						{step.unmetCriteria?.length
							? ` · ${step.unmetCriteria.length} unmet`
							: ""}
					</div>
				) : null}
			</div>
		</button>
	);
}

/**
 * Floating collapsible tracker pinned top-right (parent must be `relative`).
 * Renders only while a run is active; vanishes on completion, leaving just
 * transcript activity.
 */
export function WorkflowTrackerCard({
	run,
	clericalThreadId,
}: {
	run: TrackerRun;
	clericalThreadId: string;
}) {
	const cancelWorkflow = useMutation(api.agent.index.cancelWorkflow);
	const [collapsed, setCollapsed] = useState(false);
	const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
	const badge = STATUS_BADGE[run.status];

	return (
		<div className="pointer-events-none absolute right-3 top-3 z-20 flex max-w-[calc(100%-1.5rem)] justify-end">
			<div className="pointer-events-auto w-[19rem] max-w-full overflow-hidden rounded-xl border bg-background/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80">
				<div className="flex items-center gap-2 px-3 py-2.5">
					<WorkflowIcon className="size-4 shrink-0 text-primary" />
					<div className="min-w-0 flex-1">
						<div className="truncate text-xs font-semibold">Workflow</div>
						{run.matterFolderName ? (
							<div className="truncate text-[11px] text-muted-foreground">
								{run.matterFolderName}
							</div>
						) : null}
					</div>
					<span
						className={cn(
							"shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold",
							badge.cls,
						)}
					>
						{badge.label}
					</span>
					<button
						type="button"
						onClick={() => setCollapsed((c) => !c)}
						className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
						aria-label={collapsed ? "Expand" : "Collapse"}
					>
						<ChevronDownIcon
							className={cn(
								"size-4 transition-transform",
								collapsed && "-rotate-90",
							)}
						/>
					</button>
				</div>

				{collapsed ? null : (
					<>
						<ScrollArea className="max-h-[min(60vh,28rem)] border-t">
							<div className="space-y-0.5 p-1.5">
								{run.steps.map((step) => (
									<StepRow key={step.name} step={step} />
								))}
							</div>
						</ScrollArea>

						<div className="border-t px-2 py-1.5">
							<Button
								size="sm"
								variant="ghost"
								onClick={() => setConfirmCancelOpen(true)}
								className="h-7 w-full gap-1.5 text-xs text-muted-foreground"
							>
								<OctagonXIcon className="size-3.5" />
								Cancel workflow
							</Button>
						</div>
					</>
				)}
			</div>

			<AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
				<AlertDialogContent className="pointer-events-auto">
					<AlertDialogHeader>
						<AlertDialogTitle>Cancel this workflow?</AlertDialogTitle>
						<AlertDialogDescription>
							Workflow
							{run.matterFolderName ? ` for ${run.matterFolderName}` : ""} will
							stop where it is. Work already completed is kept, but any
							in-progress step won't finish and you'll need to start a new run
							to continue.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Keep running</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={() => {
								void cancelWorkflow({
									clericalThreadId,
									workflowRunId: run.runId,
								});
							}}
						>
							Cancel workflow
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
